import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canChangeStatus, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { getAgentSalespersonId } from '@/services/agent';
import { recordAudit } from '@/services/audit';

export interface WarrantyRecord {
  id: string;
  warrantyNumber: string;
  customerName: string;
  customerCity: string;
  productName: string;
  sizeLabel: string;
  reportedReason: string;
  warrantyPeriod: number;
  expiresAt: string;
  reviewStatus: string;
  resolutionType: string | null;
  salespersonName: string;
}

export interface WarrantiesResult {
  data: WarrantyRecord[];
}

const DEFAULT_WARRANTY_PERIOD_DAYS = 90;

export async function fetchWarranties(): Promise<WarrantiesResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [warrantiesRes, customersRes, variantsRes, productsRes, sizesRes, salesRes, salespersonsRes] =
      await Promise.all([
        supabase.from('warranties').select('*'),
        supabase.from('customers').select('id, first_name, last_name, city'),
        supabase.from('product_variants').select('*'),
        supabase.from('products').select('id, name'),
        supabase.from('sizes').select('*'),
        supabase.from('sales').select('id, salesperson_id'),
        supabase.from('salespersons').select('id, full_name'),
      ]);

    if (warrantiesRes.error) throw new Error(warrantiesRes.error.message);
    if (customersRes.error) throw new Error(customersRes.error.message);
    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (productsRes.error) throw new Error(productsRes.error.message);
    if (sizesRes.error) throw new Error(sizesRes.error.message);

    const warranties = (warrantiesRes.data ?? []) as Array<Record<string, any>>;

    const customerMap = new Map(
      ((customersRes.data ?? []) as Array<Record<string, any>>).map((c) => [c.id, c]),
    );
    const variantMap = new Map(
      ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
    );
    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
    );
    const sizeMap = new Map(
      ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );
    const saleSalespersonMap = new Map(
      ((salesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.salesperson_id]),
    );
    const salespersonMap = new Map(
      ((salespersonsRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.full_name]),
    );

    // Filtro por agente: solo ve las garantías de las ventas que él facturó.
    const agentSalespersonId = await getAgentSalespersonId();
    const visibleWarranties = agentSalespersonId
      ? warranties.filter((w) => saleSalespersonMap.get(w.sale_id) === agentSalespersonId)
      : warranties;

    const data = visibleWarranties
      .map((w) => {
        const customer = customerMap.get(w.customer_id);
        const variant = variantMap.get(w.variant_id);
        const productName = variant ? productMap.get(variant.product_id) ?? '' : '';
        const size = variant ? sizeMap.get(variant.size_id) : undefined;
        const spId = saleSalespersonMap.get(w.sale_id);
        return {
          id: w.id,
          warrantyNumber: w.warranty_number,
          customerName: customer
            ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
            : '',
          customerCity: customer?.city ?? '',
          productName,
          sizeLabel: size?.name ?? '',
          reportedReason: w.reason ?? '',
          warrantyPeriod: DEFAULT_WARRANTY_PERIOD_DAYS,
          expiresAt: w.expires_at,
          reviewStatus: w.review_status,
          resolutionType: w.resolution_type ?? null,
          salespersonName: spId ? salespersonMap.get(spId) ?? '' : '',
        };
      })
      .sort((a, b) => (a.expiresAt < b.expiresAt ? 1 : -1));

    return { data };
  } catch (err) {
    console.error('[warranties] Supabase connection failed:', err);
    throw err;
  }
}

// Entrada del formulario de reclamo de garantía (crear).
export interface WarrantyInput {
  saleId: string;
  customerId: string;
  variantId: string;
  reason: string;
  days: number;
}

export interface CreateWarrantyResult {
  success: boolean;
  warrantyId?: string;
  warrantyNumber?: string;
  error?: string;
}

// Registra un reclamo de garantía en Supabase.
export async function createWarranty(input: WarrantyInput): Promise<CreateWarrantyResult> {
  if (!(await canWrite('warranties'))) return { success: false, error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado.' };
  }

  const warrantyNumber = `G-${Date.now().toString().slice(-6)}`;
  const expiresAt = new Date(Date.now() + (input.days || 90) * 24 * 60 * 60 * 1000).toISOString();

  try {
    const { data, error } = await supabase
      .from('warranties')
      .insert({
        warranty_number: warrantyNumber,
        sale_id: input.saleId,
        customer_id: input.customerId,
        variant_id: input.variantId,
        review_status: 'pending',
        reason: input.reason.trim(),
        expires_at: expiresAt,
      })
      .select('id');

    if (error) return { success: false, error: error.message };
    const warranty = data?.[0] as { id: string } | undefined;
    if (!warranty) return { success: false, error: 'No se pudo crear el reclamo.' };

    recordAudit({
      entityType: 'warranties',
      entityId: warranty.id,
      action: 'CREATE',
      newValue: warrantyNumber,
    });

    return { success: true, warrantyId: warranty.id, warrantyNumber };
  } catch (err) {
    console.warn('[warranties] createWarranty failed:', err);
    return { success: false, error: 'Error de conexión al crear el reclamo.' };
  }
}

// Cambia el estado de revisión de una garantía (pending / under_review /
// approved / rejected / delivered) dejando registro en auditoría.
export async function updateWarrantyStatus(
  id: string,
  reviewStatus: string,
): Promise<{ error: string | null }> {
  if (!(await canChangeStatus('warranties'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { data: current } = await supabase
    .from('warranties')
    .select('review_status')
    .eq('id', id)
    .maybeSingle();

  const { error } = await supabase
    .from('warranties')
    .update({ review_status: reviewStatus })
    .eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'warranties',
    entityId: id,
    action: 'UPDATE',
    fieldName: 'review_status',
    oldValue: (current as Record<string, any> | null)?.review_status ?? '',
    newValue: reviewStatus,
  });
  return { error: null };
}